import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  FormFeedback,
  UncontrolledTooltip,
} from "reactstrap";
import * as Yup from "yup";
import { PatternFormat } from "react-number-format";
import { submitCandidateFilterData } from "model/CandidateModel";
import {
  convertToMMDDYYYY,
  toPhoneDigits,
  urlEndPoint,
  US_PHONE_INPUT_FORMAT,
} from "helpers/GeneralHelper";
import AddEditCandidateModal from "pages/AllCommonModal/AddEditCandidateModal/add-edit-candidate-modal";
import Loaders from "components/Common/Loaders";

const newCandidatePhoneSearchSchema = Yup.object().shape({
  phone: Yup.string()
    .trim()
    .required("Phone number is required")
    .matches(/^\d{10}$/, "Phone number must be 10 digits"),
});

const NewCandidate = () => {
  const phoneSearchInputRef = useRef(null);
  const shouldFocusPhoneAfterCreateRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [phoneSearchError, setPhoneSearchError] = useState("");
  const [companyData, setCompanyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);
  const [candidateModalMode, setCandidateModalMode] = useState("edit");
  const [modalCandidate, setModalCandidate] = useState({});

  const handleSearchSubmit = async (value = "") => {
    const phoneDigits = toPhoneDigits(value || searchQuery).trim();
    try {
      await newCandidatePhoneSearchSchema.validate({ phone: phoneDigits });
      setPhoneSearchError("");
    } catch (validationError) {
      setPhoneSearchError(validationError?.message || "Phone number must be 10 digits");
      setCompanyData([]);
      return;
    }
    await getCandidateList(phoneDigits);
  };

  const getCandidateList = async (phoneValue) => {
    const q = String(phoneValue || "").trim();
    if (!q) {
      return;
    }
    setLoading(true);
    const payload = {
      phone: q,
    };
    const response = await submitCandidateFilterData(payload);
    const responseData = response?.data || {};
    const listData = responseData?.records || responseData || [];
    const records = Array.isArray(listData) ? listData : [];
    setCompanyData(records);
    setLoading(false);
    if (!records.length) {
      openCandidateModal({ phone: q }, "create");
    }
  };

  const openCallDetailsInNewTab = (candidate = {}) => {
    const candidateId = candidate?.id || candidate?.candidateId;
    if (!candidateId) return;
    window.open(`/${urlEndPoint.callDetails}/${candidateId}`, "_blank", "noopener,noreferrer");
  };

  const openCandidateModal = (candidate = {}, mode = "edit") => {
    const phoneFromSearch = toPhoneDigits(searchQuery).trim();
    setCandidateModalMode(mode);
    setModalCandidate({
      ...(candidate || {}),
      phone: candidate?.phone || phoneFromSearch,
    });
    setCandidateModalOpen(true);
  };

  const closeCandidateModal = () => {
    setCandidateModalOpen(false);
    setModalCandidate({});
    setCandidateModalMode("edit");
  };

  const getSelectedCandidateName = (candidate = {}) =>
    `${candidate?.firstName || ""} ${candidate?.lastName || ""}`.trim() || "-";

  const getFirstName = (candidate = {}) =>
    String(candidate?.firstName || "").trim() || "Candidate";

  const normalizeStatusKey = (status = "") =>
    String(status || "").toLowerCase().replace(/\s+/g, "");

  const getStatusClass = (status = "") => {
    const key = normalizeStatusKey(status);
    if (key === "active" || key === "activated" || key === "activate") {
      return "candidate-status-active";
    }
    if (
      key === "deactive" ||
      key === "inactive" ||
      key === "deactivate" ||
      key === "deactivated"
    ) {
      return "candidate-status-inactive";
    }
    return "candidate-status-neutral";
  };

  const getCandidateCardClass = (candidate = {}) => {
    const key = normalizeStatusKey(candidate?.status ?? candidate?.Status ?? "");
    if (candidate?.isActive === false || candidate?.active === false) {
      return "candidate-profile-card--inactive";
    }
    if (candidate?.isActive === true || candidate?.active === true) {
      return "candidate-profile-card--active";
    }
    if (key === "active" || key === "activated" || key === "activate") {
      return "candidate-profile-card--active";
    }
    if (
      key === "deactive" ||
      key === "inactive" ||
      key === "deactivate" ||
      key === "deactivated"
    ) {
      return "candidate-profile-card--inactive";
    }
    return "candidate-profile-card--neutral";
  };

  const getCandidatePrimaryRole = (candidate = {}) =>
    candidate?.jobName || candidate?.position || candidate?.designation || "Candidate";

  const getCandidateProfileLink = (candidate = {}) =>
    candidate?.url ||
    candidate?.link ||
    candidate?.profileUrl ||
    candidate?.resumeLink ||
    candidate?.linkedin ||
    "-";

  const getRecruiterContextName = (candidate = {}) =>
    String(
      candidate?.recruiterName ||
      candidate?.userName ||
      candidate?.assignedToName ||
      candidate?.recruiter ||
      "Recruiter"
    ).trim() || "Recruiter";

  const getRelationshipToRecruiter = (candidate = {}) =>
    String(
      candidate?.relationshipType || candidate?.relationship || candidate?.relationToRecruiter || ""
    ).trim() || "—";

  const getKnownForDisplay = (candidate = {}) => {
    const y =
      candidate?.knownForYears ??
      candidate?.yearsKnown ??
      candidate?.tenureYears ??
      candidate?.knownFor;
    if (y !== undefined && y !== null && String(y).trim() !== "") {
      const n = String(y).trim();
      return /year/i.test(n) ? n : `${n} year${n === "1" ? "" : "s"}`;
    }
    return "—";
  };

  const getLocationLine = (candidate = {}) => {
    const client = String(candidate?.clientName || candidate?.client || "").trim();
    const loc =
      [candidate?.city, candidate?.state, candidate?.country]
        .filter(Boolean)
        .map((s) => String(s).trim())
        .filter(Boolean)
        .join(", ") || "";
    if (client && loc) return `${client}, ${loc}`;
    return client || loc || "—";
  };

  const candidateCards = useMemo(
    () => (Array.isArray(companyData) ? companyData : []),
    [companyData]
  );

  const focusPhoneSearchInput = () => {
    const root = phoneSearchInputRef.current;
    const inputEl =
      root?.tagName === "INPUT"
        ? root
        : root?.querySelector?.("input") || root;
    if (inputEl && typeof inputEl.focus === "function") {
      inputEl.focus({ preventScroll: true });
      if (typeof inputEl.select === "function") {
        inputEl.select();
      }
    }
  };

  const handleClearPhoneSearch = () => {
    setSearchQuery("");
    setCompanyData([]);
    setPhoneSearchError("");
    window.requestAnimationFrame(() => {
      focusPhoneSearchInput();
    });
  };

  /** Focus phone field after add modal fully closes (backdrop was stealing focus at 0ms). */
  useEffect(() => {
    if (candidateModalOpen || !shouldFocusPhoneAfterCreateRef.current) return;
    shouldFocusPhoneAfterCreateRef.current = false;
    const frameId = window.requestAnimationFrame(() => {
      focusPhoneSearchInput();
    });
    const timeoutId = window.setTimeout(focusPhoneSearchInput, 150);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [candidateModalOpen]);

  const handleModalSuccess = () => {
    if (candidateModalMode === "create") {
      setSearchQuery("");
      setCompanyData([]);
      setPhoneSearchError("");
      shouldFocusPhoneAfterCreateRef.current = true;
      return;
    }
    const q = toPhoneDigits(searchQuery).trim();
    if (q) {
      getCandidateList(q);
    }
  };

  return (
    <div className="page-content new-candidate-page">
      <Container fluid>
        <Card className="border mb-3 new-candidate-search-card">
          <CardBody>
            {/* <Row className="align-items-center g-3 mb-0 mb-md-2">
              <Col xs="12" md="4" lg="3">
                <h5 className="fw-bold mb-0">New Candidate</h5>
              </Col>
              <Col xs="12" md="8" lg="6">
                <div className="d-flex border rounded w-100 category-search-input">
                  <Input
                    type="text"
                    placeholder="Search by phone"
                    className="border-0"
                    value={searchQuery}
                    maxLength={10}
                    onChange={(e) => {
                      const nextValue = allowOnlyNumber(e?.target?.value || "");
                      setSearchQuery(nextValue);
                      if (!nextValue.trim()) {
                        setCompanyData([]);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit(searchQuery?.trim());
                      }
                    }}
                  />
                  <Button
                    className="search-icon-button"
                    onClick={() => handleSearchSubmit(searchQuery?.trim())}
                  >
                    <i className="fa fa-search" />
                  </Button>
                </div>
              </Col>
            </Row> */}
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-3">New Candidate</h5>
            </div>
            <hr />

            <Row className="align-items-center g-2 mb-4">
              <Col xs="12" md="8">
                <div className="d-flex align-items-stretch gap-2">
                  <div
                    className={`d-flex border rounded flex-grow-1 category-search-input${phoneSearchError ? " border-danger" : ""
                      }`}
                  >
                    <PatternFormat
                      customInput={Input}
                      getInputRef={phoneSearchInputRef}
                      type="text"
                      placeholder="Enter phone number"
                      className="border-0"
                      format={US_PHONE_INPUT_FORMAT}
                      value={searchQuery}
                      invalid={!!phoneSearchError}
                      onValueChange={(values) => {
                        const nextValue = toPhoneDigits(values?.value || "");
                        setSearchQuery(nextValue);
                        if (phoneSearchError) setPhoneSearchError("");
                        if (!nextValue) {
                          setCompanyData([]);
                        }
                      }}
                      onPaste={(e) => {
                        // PatternFormat truncates to 10 digits before onValueChange;
                        // intercept paste so +1 country code is stripped first.
                        const pasted = e.clipboardData?.getData("text") || "";
                        const digits = String(pasted).replace(/\D/g, "");
                        if (digits.length === 11 && digits.startsWith("1")) {
                          e.preventDefault();
                          const nextValue = toPhoneDigits(pasted);
                          setSearchQuery(nextValue);
                          if (phoneSearchError) setPhoneSearchError("");
                          if (!nextValue) setCompanyData([]);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery);
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery)}
                    >
                      <i className="fa fa-search" />
                    </Button>
                  </div>

                  {candidateCards.length ? (
                    <Button
                      type="button"
                      className="new-candidate-search-close-btn"
                      onClick={handleClearPhoneSearch}
                      aria-label="Clear phone number"
                      color="danger"
                    >
                     <span>Close </span><i className="fa fa-times" aria-hidden />
                    </Button>
                  ) : null}
                </div>
                {phoneSearchError ? (
                  <FormFeedback className="d-block">{phoneSearchError}</FormFeedback>
                ) : null}
              </Col>

            </Row>

            {loading ? (
              <Loaders />
            ) : candidateCards?.length ? (
              <Row className="g-3 new-candidate-card-row">
                {candidateCards.map((candidate, idx) => {
                  const cardKey =
                    candidate?.id || candidate?.candidateId || `idx-${idx}`;
                  const recruiterName = getRecruiterContextName(candidate);
                  const firstName = getFirstName(candidate);
                  return (
                    <Col
                      key={cardKey}
                      xs="12"
                      sm="6"
                      lg="4"
                    >
                      <Card
                        className={`candidate-profile-card h-100 ${getCandidateCardClass(candidate)}`}
                      >
                        <CardBody className="d-flex flex-column p-0">
                          <div className="candidate-card-top px-3 pt-3 pb-2">
                            <div className="candidate-top-row">
                              <h4 className="candidate-profile-name mb-0 text-start">
                                {getSelectedCandidateName(candidate)}
                              </h4>
                              <div className="d-flex align-items-center gap-1 candidate-card-icon-actions flex-shrink-0">
                                <span
                                  className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                  onClick={() => openCallDetailsInNewTab(candidate)}
                                  id={`new-candidate-call-${cardKey}`}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      openCallDetailsInNewTab(candidate);
                                    }
                                  }}
                                >
                                  <i className="mdi mdi-phone-log text-success" aria-hidden />
                                </span>
                                <UncontrolledTooltip target={`new-candidate-call-${cardKey}`}>
                                  Call details
                                </UncontrolledTooltip>
                              </div>
                              <span
                                className={`candidate-status-badge candidate-status-pill-compact text-capitalize ${getStatusClass(
                                  candidate?.status
                                )}`}
                              >
                                {candidate?.status || "—"}
                              </span>
                            </div>

                            <div className="candidate-contact-stack">
                              <div className="candidate-contact-line">
                                <span className="candidate-card-subtitle candidate-card-left-text mb-0">
                                  {getCandidatePrimaryRole(candidate)}
                                </span>
                              </div>
                              <div className="candidate-contact-line">
                                <a
                                  className="candidate-card-link-row"
                                  href={
                                    candidate?.email
                                      ? `mailto:${candidate.email}`
                                      : undefined
                                  }
                                  onClick={(e) => {
                                    if (!candidate?.email) e.preventDefault();
                                  }}
                                >
                                  <i className="fa fa-envelope candidate-card-contact-icon" aria-hidden />
                                  <span className="candidate-card-left-text">{candidate?.email || "—"}</span>
                                </a>
                              </div>
                              <div className="candidate-contact-line">
                                <a
                                  className="candidate-card-link-row"
                                  href={
                                    candidate?.phone
                                      ? `tel:${String(candidate.phone).replace(/\D/g, "")}`
                                      : undefined
                                  }
                                  onClick={(e) => {
                                    if (!candidate?.phone) e.preventDefault();
                                  }}
                                >
                                  <i className="fa fa-phone candidate-card-contact-icon" aria-hidden />
                                  <span className="candidate-card-left-text">{candidate?.phone || "—"}</span>
                                </a>
                              </div>
                              {/* <div className="candidate-contact-line">
                                <span className="candidate-card-location candidate-card-left-text mb-0">
                                  {candidate?.clientName || "—"}
                                </span>
                              </div> */}
                            </div>
                          </div>

                          <div className="candidate-card-divider" />

                          {/* <div className="candidate-card-bottom px-3 pt-2 pb-2">
                            <p className="candidate-card-relation-label mb-1">
                              Relationship to {recruiterName}
                            </p>
                            <p className="candidate-card-relation-value mb-3">
                              {getRelationshipToRecruiter(candidate)}
                            </p>
                            <p className="candidate-card-relation-label mb-1">
                              {firstName} has known {recruiterName} for
                            </p>
                            <p className="candidate-card-relation-value mb-0">
                              {getKnownForDisplay(candidate)}
                            </p>
                          </div> */}

                          <div className="candidate-card-meta px-3 py-2 flex-grow-1">
                            <div className="candidate-card-meta-row">
                              <span>Client</span>
                              <strong>{candidate?.clientName || "—"}</strong>
                            </div>
                            <div className="candidate-card-meta-row">
                              <span>Source</span>
                              <strong>{candidate?.sourceName || "—"}</strong>
                            </div>
                            <div className="candidate-card-meta-row">
                              <span>Order status</span>
                              <strong>{candidate?.orderStatus || "—"}</strong>
                            </div>
                            <div className="candidate-card-meta-row">
                              <span>Created</span>
                              <strong>
                                {candidate?.createAt
                                  ? convertToMMDDYYYY(candidate?.createAt)
                                  : "—"}
                              </strong>
                            </div>
                            <div className="candidate-card-meta-row">
                              <span>Update Send</span>
                              <strong>
                                {candidate?.updateSendDate
                                  ? convertToMMDDYYYY(candidate?.updateSendDate)
                                  : "—"}
                              </strong>
                            </div>
                            {/* <div className="candidate-card-meta-row">
                              <span>URL</span>
                              <strong className="text-break text-end">
                                {getCandidateProfileLink(candidate)}
                              </strong>
                            </div> */}
                          </div>
                          {/* {(candidate?.status === 'active' || candidateCards?.length===1 ) 
                          ? */}
                            <div className="px-3 pb-3 pt-1 mt-auto d-flex gap-2 flex-nowrap candidate-card-actions">
                              <Button
                                color="primary"
                                className="btn-sm-height candidate-card-btn candidate-card-btn-edit"
                                onClick={() => openCandidateModal(candidate, "edit")}
                              >
                                Edit
                              </Button>
                              <Button
                                color="success"
                                className="btn-sm-height candidate-card-btn candidate-card-btn-transfer"
                                onClick={() => openCandidateModal(candidate, "transfer")}
                              >
                                Transfer to New Client
                              </Button>
                            </div> 
                            {/* : null
                          } */}
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              // <Card className="shadow-sm">
              //   <CardBody className="d-flex justify-content-between align-items-center">
              //     <span>No candidate found.</span>
              //   </CardBody>
              // </Card>
              null
            )}
          </CardBody>
        </Card>

        <AddEditCandidateModal
          isOpen={candidateModalOpen}
          onClose={closeCandidateModal}
          mode={candidateModalMode}
          candidate={modalCandidate}
          prefilledPhone={toPhoneDigits(searchQuery).trim()}
          clientSelectPageName="NewCandidate"
          onSuccess={handleModalSuccess}
        />
      </Container>
    </div>
  );
};

export default NewCandidate;
